import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchChoiceMappings, deleteMapping } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { ChoiceMappingDTO } from "@shared/api-types";
import ChoiceMappingForm from "./choice-mapping-form";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ChevronLeft, ChevronRight, Plus, Trash2, Edit } from "lucide-react";

interface ChoiceMappingsTableProps {
  onAddMapping: () => void;
}

const ChoiceMappingsTable = ({ onAddMapping }: ChoiceMappingsTableProps) => {
  const [entityFilter, setEntityFilter] = useState<string>("all_entities");
  const [fieldFilter, setFieldFilter] = useState<string>("all_fields");
  const [currentPage, setCurrentPage] = useState(1);
  const [mappingToEdit, setMappingToEdit] = useState<ChoiceMappingDTO | null>(null);
  const [mappingToDelete, setMappingToDelete] = useState<ChoiceMappingDTO | null>(null);
  const itemsPerPage = 10;
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["/api/choice-mappings", entityFilter, fieldFilter],
    queryFn: () =>
      fetchChoiceMappings(
        entityFilter === "all_entities" ? undefined : entityFilter,
        fieldFilter === "all_fields" ? undefined : fieldFilter,
      ),
  });

  const deleteMutation = useMutation({
    mutationFn: ({ partitionKey, rowKey }: { partitionKey: string; rowKey: string }) =>
      deleteMapping(partitionKey, rowKey),
    onSuccess: () => {
      toast({
        title: "Mapping deleted",
        description: "The choice mapping has been deleted successfully.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/choice-mappings"] });
      queryClient.invalidateQueries({ queryKey: ["/api/field-mappings"] });
      setMappingToDelete(null);
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to delete the mapping.",
        variant: "destructive",
      });
    },
  });

  const handleDeleteClick = (mapping: ChoiceMappingDTO) => {
    setMappingToDelete(mapping);
  };

  const handleConfirmDelete = () => {
    if (mappingToDelete) {
      deleteMutation.mutate({
        partitionKey: mappingToDelete.partitionKey,
        rowKey: mappingToDelete.rowKey,
      });
    }
  };

  const handleEditClick = (mapping: ChoiceMappingDTO) => {
    setMappingToEdit(mapping);
  };

  const closeDeleteDialog = () => {
    setMappingToDelete(null);
  };

  const closeEditModal = () => {
    setMappingToEdit(null);
  };

  const uniqueEntityNames = Array.from(
    new Set(data?.map((mapping) => mapping.entityName) || []),
  ).sort();

  const uniqueFieldNames = Array.from(
    new Set(data?.map((mapping) => mapping.fieldName) || []),
  ).sort();

  const totalItems = data?.length || 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const handlePageChange = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = data?.slice(startIndex, endIndex);

  if (isError) {
    return (
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h3 className="text-red-500">Error loading choice mappings: {String(error)}</h3>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6 mb-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold">Choice Mappings</h2>
        <div className="flex space-x-4">
          <div>
            <Select value={entityFilter} onValueChange={setEntityFilter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Filter by entity" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all_entities">All Entities</SelectItem>
                {uniqueEntityNames.map((entity) => (
                  <SelectItem key={entity} value={entity}>
                    {entity}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Select value={fieldFilter} onValueChange={setFieldFilter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Filter by field" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all_fields">All Fields</SelectItem>
                {uniqueFieldNames.map((field) => (
                  <SelectItem key={field} value={field}>
                    {field}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={onAddMapping} className="flex gap-1">
            <Plus size={16} />
            Add Mapping
          </Button>
        </div>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead>Entity</TableHead>
              <TableHead>Field</TableHead>
              <TableHead>KUP Value</TableHead>
              <TableHead>HS Value</TableHead>
              <TableHead>HS Label</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array(5)
                .fill(0)
                .map((_, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <Skeleton className="h-4 w-20" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-32" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-24" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-24" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-24" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-16" />
                    </TableCell>
                  </TableRow>
                ))
            ) : paginatedData?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-4">
                  No choice mappings found
                </TableCell>
              </TableRow>
            ) : (
              paginatedData?.map((mapping) => (
                <TableRow key={mapping.rowKey}>
                  <TableCell>{mapping.entityName}</TableCell>
                  <TableCell>{mapping.fieldName}</TableCell>
                  <TableCell>{mapping.kupValue}</TableCell>
                  <TableCell>{mapping.hsValue}</TableCell>
                  <TableCell>{mapping.hsLabel}</TableCell>
                  <TableCell className="text-right space-x-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEditClick(mapping)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteClick(mapping)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {data && data.length > itemsPerPage && (
          <div className="flex items-center justify-between px-4 py-3 bg-gray-50">
            <div className="text-sm text-gray-700">
              Showing <span className="font-medium">{startIndex + 1}</span> to {" "}
              <span className="font-medium">{Math.min(endIndex, totalItems)}</span> of {" "}
              <span className="font-medium">{totalItems}</span> results
            </div>
            <div className="flex-1 flex justify-end">
              <div className="flex gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage <= 1}
                >
                  <span className="sr-only">Go to first page</span>
                  <ChevronLeft className="h-4 w-4" />
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage <= 1}
                >
                  <span className="sr-only">Go to previous page</span>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                >
                  <span className="sr-only">Go to next page</span>
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage >= totalPages}
                >
                  <span className="sr-only">Go to last page</span>
                  <ChevronRight className="h-4 w-4" />
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {mappingToDelete && (
        <AlertDialog open={!!mappingToDelete} onOpenChange={closeDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Confirm Deletion</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete the mapping for {" "}
                <strong>
                  {mappingToDelete.entityName}.{mappingToDelete.fieldName}
                </strong>
                ? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleConfirmDelete}>
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      {mappingToEdit && (
        <ChoiceMappingForm
          mapping={mappingToEdit}
          isOpen={!!mappingToEdit}
          onClose={closeEditModal}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ["/api/choice-mappings"] });
            queryClient.invalidateQueries({ queryKey: ["/api/field-mappings"] });
            closeEditModal();
          }}
        />
      )}
    </div>
  );
};

export default ChoiceMappingsTable;
